import React from 'react';
import { ArrowRight, Heart, Users, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const CustomerStories = () => {
  // HR Leaders Stories for "From Leaders, with ❤️" section
  const leaderStories = [
    {
      name: "Adaeze Okonkwo",
      role: "Chief People Officer",
      company: "Nexgen Financial Services",
      before: "Managing 500+ employees across 12 branches with manual spreadsheets was overwhelming. I spent more time on admin than strategy.",
      after: "HRMQore transformed everything. What used to take weeks now happens in hours. We've reclaimed time to focus on what matters—our people."
    },
    {
      name: "Chukwuemeka Eze",
      role: "Head of Talent Acquisition",
      company: "TechVenture Africa",
      before: "Rapid growth was causing onboarding chaos. New hires waited weeks for paperwork, losing their initial enthusiasm.",
      after: "We scaled from 50 to 250 employees in 18 months without adding HR staff. Our onboarding is now praised as best-in-class."
    },
    {
      name: "Funke Adeyemi",
      role: "HR Director",
      company: "GreenLeaf Manufacturing",
      before: "With 1,200 employees across three factories, inconsistent processes were our biggest challenge. Compliance was a constant worry.",
      after: "The real-time analytics changed everything. We went from reactive to proactive HR management overnight. 100% compliance rate now."
    }
  ];

  // Employee Stories for "Individual Voices" section
  const employeeStories = [
    {
      name: "Ngozi Ibe",
      role: "Senior Developer",
      company: "CodeCraft Studios",
      before: "I used to dread leave requests. Fill this form, wait for approval, follow up endlessly...",
      after: "Now I request time off from my phone in 30 seconds. Approved in minutes. I actually take vacations now!"
    },
    {
      name: "Olumide Johnson",
      role: "Finance Manager",
      company: "Lagos Logistics Ltd",
      before: "Expense reimbursements took 2-3 weeks. Staff were frustrated, and so was I...",
      after: "The system is revolutionary. Instant access to information, zero delays. My team trusts the process now."
    },
    {
      name: "Amina Mohammed",
      role: "Compliance Officer",
      company: "SecureBank Nigeria",
      before: "Policy acknowledgments were a nightmare. Chasing signatures, filing papers, audit anxiety...",
      after: "Every policy update is tracked digitally. 100% compliance, zero stress. I actually enjoy audits now."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-[#0052CC]">
        <div className="absolute top-20 right-10 w-96 h-96 bg-[#0066FF]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-64 h-64 bg-[#0052CC]/20 rounded-full blur-3xl" />
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <Badge className="bg-white/10 text-white hover:bg-white/20 border-0 mb-6 px-4 py-2">
            <Heart className="w-4 h-4 mr-2 inline text-red-400" />
            Success Stories
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight">
            Real Stories from{' '}
            <span className="bg-gradient-to-r from-[#0066FF] to-[#60a5fa] bg-clip-text text-transparent">
              Real People
            </span>
          </h1>
          <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Discover how HR leaders and employees across industries are transforming their workplaces with HRMQore.
          </p>
        </div>
      </section>

      {/* From Leaders, with ❤️ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#0066FF]/10 text-[#0066FF] hover:bg-[#0066FF]/20 border-0 mb-4">
              <Award className="w-4 h-4 mr-2 inline" />
              Leadership Perspectives
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              From Leaders, With{' '}
              <span className="text-red-500">❤️</span>
            </h2>
            <p className="text-lg text-gray-600">
              HR leaders share their transformation journey with HRMQore
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {leaderStories.map((person, idx) => (
              <Card key={idx} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                <CardContent className="p-0">
                  {/* Before */}
                  <div className="bg-gray-100 p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Before</span>
                    </div>
                    <p className="text-gray-600 text-sm italic">&quot;{person.before}&quot;</p>
                  </div>
                  
                  {/* After */}
                  <div className="bg-white p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-xs font-semibold text-green-600 uppercase tracking-wider">After</span>
                    </div>
                    <p className="text-gray-800 text-sm font-medium">&quot;{person.after}&quot;</p>
                    
                    {/* Author - Name and Organization only, no avatar */}
                    <div className="mt-6 pt-4 border-t border-gray-100">
                      <div className="font-semibold text-gray-900 text-sm">{person.name}</div>
                      <div className="text-xs text-gray-500">{person.role}, {person.company}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Testimonial Banner */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-3xl p-10 md:p-14 text-white text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              <Award className="w-12 h-12 mx-auto mb-6 text-yellow-400" />
              <blockquote className="text-2xl md:text-3xl font-medium leading-relaxed mb-6 max-w-3xl mx-auto">
                &quot;HRMQore isn&apos;t just software—it&apos;s a partnership. Their team understands HR challenges because they&apos;ve lived them.&quot;
              </blockquote>
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="font-semibold text-lg">Sarah Okonkwo</div>
                  <div className="text-white/70">VP of Human Resources, Continental Bank</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Individual Voices Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#0066FF]/10 text-[#0066FF] hover:bg-[#0066FF]/20 border-0 mb-4">
              <Users className="w-4 h-4 mr-2 inline" />
              Individual Voices
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              From Employees, With{' '}
              <span className="text-red-500">❤️</span>
            </h2>
            <p className="text-lg text-gray-600">
              The real measure of success: happier people at work
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {employeeStories.map((person, idx) => (
              <Card key={idx} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                <CardContent className="p-0">
                  {/* Before */}
                  <div className="bg-gray-100 p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Before</span>
                    </div>
                    <p className="text-gray-600 text-sm italic">&quot;{person.before}&quot;</p>
                  </div>
                  
                  {/* After */}
                  <div className="bg-white p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-xs font-semibold text-green-600 uppercase tracking-wider">After</span>
                    </div>
                    <p className="text-gray-800 text-sm font-medium">&quot;{person.after}&quot;</p>
                    
                    {/* Author - Name and Organization only, no avatar */}
                    <div className="mt-6 pt-4 border-t border-gray-100">
                      <div className="font-semibold text-gray-900 text-sm">{person.name}</div>
                      <div className="text-xs text-gray-500">{person.role}, {person.company}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Ready to Write Your Story - CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-12 md:p-16 text-white relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#0066FF]/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#0066FF]/10 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Write{' '}
                <span className="bg-gradient-to-r from-[#0066FF] to-[#60a5fa] bg-clip-text text-transparent">
                  Your Story?
                </span>
              </h2>
              <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
                Join the community of forward-thinking companies transforming how they manage and empower their people. Your transformation story starts with a conversation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white hover:opacity-90 px-8"
                  onClick={() => window.location.href = '/contact'}
                >
                  Start Your Story
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-gray-600 text-white hover:bg-white/10"
                  onClick={() => window.location.href = '/pricing'}
                >
                  Explore Plans
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomerStories;
